/**
 * Brand colour helpers.
 *
 * Validates stored hex values and applies token maps. Tenant theme derivation
 * lives in branding.js, which supplies readable brand, navigation, and surface
 * colors for light and dark appearances. CSS supplies the initial defaults.
 *
 * See `src/styles/README.md` for the derivation itself.
 */

/**
 * The product brand, used when a tenant has not chosen one: the Graphit
 * emblem's steel blue in its text-safe shade (4.5:1 on white; white text on it
 * is about 4.9:1). See docs/brand/README.md.
 */
export const DEFAULT_BRAND = '#47749f'

/**
 * localStorage key for a tenant's last resolved branding profile, read by
 * ThemeProvider before the tenant's settings arrive. Same `pref:<scope>:<key>`
 * shape as the theme preference (`pref:global:theme`).
 *
 * @param {string|number} tenantId
 * @returns {string}
 */
export function brandCacheKey(tenantId) {
  return `pref:tenant:${tenantId}:brand`
}

const HEX6 = /^#([0-9a-f]{6})$/i
const HEX3 = /^#([0-9a-f]{3})$/i

/**
 * Normalise a hex colour to lowercase `#rrggbb`.
 *
 * @param {unknown} hex - A `#RGB` or `#RRGGBB` string; anything else is rejected
 * @returns {string|null} The normalised colour, or null when it is not a hex
 * @example
 * normalizeHex('#ABC')     // '#aabbcc'
 * normalizeHex('3b6fe0')   // null — no leading hash
 */
export function normalizeHex(hex) {
  if (typeof hex !== 'string') return null

  const candidate = hex.trim()

  if (HEX6.test(candidate)) return candidate.toLowerCase()

  const short = candidate.match(HEX3)
  if (short) {
    const [red, green, blue] = short[1].toLowerCase()
    return `#${red}${red}${green}${green}${blue}${blue}`
  }

  return null
}

/**
 * Whether a value is a hex colour this module can use.
 *
 * @param {unknown} hex
 * @returns {boolean}
 */
export function isValidHex(hex) {
  return normalizeHex(hex) !== null
}

/**
 * Legacy single-color helper for isolated consumers. The application provider
 * uses brandingTokens() to derive a complete, contrast-checked tenant scheme.
 *
 * @param {unknown} hex - The tenant's colour; invalid values fall back
 * @returns {{'--brand': string}} A new object on every call
 */
export function brandTokensFromHex(hex) {
  return { '--brand': normalizeHex(hex) ?? DEFAULT_BRAND }
}

/**
 * Write brand tokens onto an element's inline style.
 *
 * @param {Record<string, string>} tokens - A map from brandingTokens or brandTokensFromHex
 * @param {HTMLElement} [el=document.documentElement] - Target element
 * @returns {void}
 */
export function applyBrandTokens(tokens, el = document.documentElement) {
  if (!el || !tokens) return
  for (const [property, value] of Object.entries(tokens)) {
    el.style.setProperty(property, value)
  }
}
